/**
 * Instanced quad batch — entities, particles, and debug shapes.
 *
 * There is no art pipeline yet, so entities render as procedural shapes with a
 * contrasting rim. That is not a placeholder compromise: the **rim light is a
 * shipping requirement**. docs/01-architecture.md §6.4 makes the player
 * silhouette non-negotiable at maximum chaos, and a 1px rim drawn after the
 * world is how that's guaranteed regardless of what the terrain behind it is
 * doing. Art will slot into the same slot later.
 *
 * One draw call per batch, instance data in a single interleaved buffer.
 */

import { profiler } from '../core/profiler';
import { M } from '../sim/materials';
import type { Particles } from '../sim/particles';
import type { Camera } from './camera';
import { GLContext, Uniforms, createProgram } from './gl';

/** floats per instance: pos(2) size(2) color(4) rim(4) shape(1) rot(1) */
const STRIDE = 14;
const MAX_INSTANCES = 8192;

const VERT = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aCorner;
layout(location = 1) in vec2 iPos;
layout(location = 2) in vec2 iSize;
layout(location = 3) in vec4 iColor;
layout(location = 4) in vec4 iRim;
layout(location = 5) in float iShape;
layout(location = 6) in float iRot;

uniform vec2 uCamera;
uniform vec2 uViewSize;

out vec2 vLocal;
out vec4 vColor;
out vec4 vRim;
out float vShape;
out vec2 vSizePx;

void main() {
  float c = cos(iRot), s = sin(iRot);
  vec2 local = aCorner * iSize;
  vec2 rotated = vec2(local.x * c - local.y * s, local.x * s + local.y * c);
  vec2 world = iPos + rotated;
  vec2 ndc = (world - uCamera) / (uViewSize * 0.5);
  gl_Position = vec4(ndc.x, -ndc.y, 0.0, 1.0);
  vLocal = aCorner;
  vColor = iColor;
  vRim = iRim;
  vShape = iShape;
  vSizePx = iSize;
}`;

const FRAG = `#version 300 es
precision highp float;
in vec2 vLocal;
in vec4 vColor;
in vec4 vRim;
in float vShape;
in vec2 vSizePx;
out vec4 outColor;

void main() {
  vec2 p = vLocal;                 // [-1,1]
  float alpha = vColor.a;
  vec3 col = vColor.rgb;

  if (vShape > 0.5 && vShape < 1.5) {
    // circle
    float d = length(p);
    if (d > 1.0) discard;
    // Rim as a function of distance, so it survives any size.
    float rimW = vRim.a;
    if (rimW > 0.0 && d > 1.0 - rimW) col = vRim.rgb;
  } else if (vShape >= 1.5) {
    // soft blob — used for gas puffs and glow
    float d = length(p);
    if (d > 1.0) discard;
    alpha *= smoothstep(1.0, 0.15, d);
  } else {
    // rect: rim on the outer band, in units of the quad's own extent
    float rimW = vRim.a;
    if (rimW > 0.0) {
      vec2 edge = abs(p);
      // Scale the band per-axis so a tall thin body keeps a uniform 1px rim.
      vec2 band = vec2(rimW / max(vSizePx.x, 0.001), rimW / max(vSizePx.y, 0.001));
      if (edge.x > 1.0 - band.x || edge.y > 1.0 - band.y) col = vRim.rgb;
    }
  }

  outColor = vec4(col, alpha);
}`;

export const Shape = { RECT: 0, CIRCLE: 1, BLOB: 2 } as const;

export class SpriteBatch {
  private glc: GLContext;
  private gl: WebGL2RenderingContext;
  private prog: WebGLProgram;
  private uni: Uniforms;
  private vao: WebGLVertexArrayObject;
  private instanceBuf: WebGLBuffer;
  private data = new Float32Array(MAX_INSTANCES * STRIDE);
  private count = 0;
  private additive = false;

  constructor(glc: GLContext) {
    this.glc = glc;
    const gl = (this.gl = glc.gl);
    this.prog = createProgram(gl, VERT, FRAG, 'sprites');
    this.uni = new Uniforms(gl, this.prog);

    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);

    const cornerBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, cornerBuf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW,
    );
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    this.instanceBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuf);
    gl.bufferData(gl.ARRAY_BUFFER, this.data.byteLength, gl.DYNAMIC_DRAW);

    const bytes = STRIDE * 4;
    const attr = (loc: number, size: number, offset: number) => {
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, size, gl.FLOAT, false, bytes, offset * 4);
      gl.vertexAttribDivisor(loc, 1);
    };
    attr(1, 2, 0); // pos
    attr(2, 2, 2); // size
    attr(3, 4, 4); // color
    attr(4, 4, 8); // rim
    attr(5, 1, 12); // shape
    attr(6, 1, 13); // rot

    gl.bindVertexArray(null);
  }

  begin(additive = false): void {
    this.count = 0;
    this.additive = additive;
  }

  push(
    x: number,
    y: number,
    halfW: number,
    halfH: number,
    r: number,
    g: number,
    b: number,
    a: number,
    shape: number = Shape.RECT,
    rimR = 0,
    rimG = 0,
    rimB = 0,
    rimW = 0,
    rot = 0,
  ): void {
    if (this.count >= MAX_INSTANCES) return;
    const o = this.count * STRIDE;
    const d = this.data;
    d[o] = x;
    d[o + 1] = y;
    d[o + 2] = halfW;
    d[o + 3] = halfH;
    d[o + 4] = r;
    d[o + 5] = g;
    d[o + 6] = b;
    d[o + 7] = a;
    d[o + 8] = rimR;
    d[o + 9] = rimG;
    d[o + 10] = rimB;
    d[o + 11] = rimW;
    d[o + 12] = shape;
    d[o + 13] = rot;
    this.count++;
  }

  /**
   * Particles draw as material-coloured cells. Because a particle is a real
   * cell in transit (see sim/particles.ts), it must look identical to the same
   * material sitting in the grid, or the handoff is visible and the illusion
   * of conserved matter breaks.
   *
   * `p.x`/`p.y` are pushed as-is, at whatever sub-cell float position the sim
   * is currently holding — never floored or rounded to the cell the particle
   * happens to occupy (issue #151). `Particles.step` needs that float
   * precision for conservation while a cell is in transit, and rounding it
   * away here for the draw call is a read-side-only choice with no effect on
   * that math; it would just make the same continuous motion visually hitch
   * in 1-cell steps. `camera.renderX/renderY` and `uViewSize` (which already
   * carries zoom, `1/viewH`) apply the same continuous transform to this
   * position as every other layer, so it is sub-pixel accurate at any zoom
   * without this function doing anything extra.
   */
  pushParticles(p: Particles): void {
    for (let i = 0; i < p.count; i++) {
      const m = p.mat[i];
      const pr = M.palette[m * 4] / 255;
      const pg = M.palette[m * 4 + 1] / 255;
      const pb = M.palette[m * 4 + 2] / 255;
      const emis = M.emissive[m];
      // Hot particles are drawn slightly larger and brighter — a spark reads
      // at one pixel only if it glows.
      const size = emis > 0.4 ? 0.9 : 0.55;
      this.push(
        p.x[i],
        p.y[i],
        size,
        size,
        pr + emis * 0.5,
        pg + emis * 0.3,
        pb + emis * 0.1,
        1,
        emis > 0.4 ? Shape.BLOB : Shape.RECT,
      );
    }
  }

  flush(camera: Camera): void {
    if (this.count === 0) return;
    const gl = this.gl;
    profiler.begin('render');

    gl.useProgram(this.prog);
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuf);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.data.subarray(0, this.count * STRIDE));

    gl.enable(gl.BLEND);
    if (this.additive) gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    else gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    this.uni.f2('uCamera', camera.renderX, camera.renderY);
    this.uni.f2('uViewSize', camera.viewW, camera.viewH);

    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.count);
    profiler.count('drawCalls');
    profiler.count('instances', this.count);

    gl.bindVertexArray(null);
    profiler.end('render');
  }

  get instanceCount(): number {
    return this.count;
  }
}
